import { de } from "./de";
import { en } from "./en";
import type { Messages, Plural } from "./messages";

export type { Messages, Plural, HelpKey, SpeedUnit } from "./messages";

/** Every supported language; add one by writing its Messages file and listing it here. */
const MESSAGES = { en, de } satisfies Record<string, Messages>;

export type Lang = keyof typeof MESSAGES;

/** Dropdown order. */
export const LANGS = Object.keys(MESSAGES) as Lang[];

/** Used for every browser language without its own Messages file. */
export const FALLBACK_LANG: Lang = "en";

const STORAGE_KEY = "planets.lang";

export function isLang(value: unknown): value is Lang {
  return typeof value === "string" && Object.hasOwn(MESSAGES, value);
}

export function messages(lang: Lang): Messages {
  return MESSAGES[lang];
}

/** The language for a browser tag such as "de-AT": its primary subtag if supported, else English. */
export function detectLang(browserTag: string | undefined): Lang {
  const primary = browserTag?.split("-")[0].toLowerCase();
  return isLang(primary) ? primary : FALLBACK_LANG;
}

/** The stored choice, else the browser language. Storage can throw in private modes. */
export function initialLang(): Lang {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    // no storage: fall back to the browser language
  }
  return isLang(stored) ? stored : detectLang(navigator.language);
}

export function saveLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // the choice then lasts for this visit only
  }
}

/** Replaces each `{key}` in a template with its value. */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

// formatters are rebuilt per call otherwise, and the time pill formats every frame
const numberFormats = new Map<string, Intl.NumberFormat>();
const pluralRules = new Map<string, Intl.PluralRules>();

function numberFormat(
  lang: Lang,
  key: string,
  options: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const id = `${lang}:${key}`;
  let format = numberFormats.get(id);
  if (!format) {
    format = new Intl.NumberFormat(MESSAGES[lang].locale, options);
    numberFormats.set(id, format);
  }
  return format;
}

/** A number in the language's format, thousands grouped, with `minDigits` to `maxDigits` decimals. */
export function formatNumber(
  lang: Lang,
  value: number,
  maxDigits = 0,
  minDigits = maxDigits,
): string {
  return numberFormat(lang, `${minDigits}:${maxDigits}`, {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  }).format(value);
}

/** A number rounded to `digits` significant figures, trailing zeros kept: 9.80, 274, 0.00577. */
export function formatSignificant(lang: Lang, value: number, digits: number): string {
  return numberFormat(lang, `s${digits}`, {
    minimumSignificantDigits: digits,
    maximumSignificantDigits: digits,
  }).format(value);
}

/** `forms` for `count`, with `{count}` formatted as in `formatNumber`. */
export function plural(
  lang: Lang,
  forms: Plural,
  count: number,
  maxDigits = 0,
  minDigits = maxDigits,
): string {
  let rules = pluralRules.get(lang);
  if (!rules) {
    rules = new Intl.PluralRules(MESSAGES[lang].locale);
    pluralRules.set(lang, rules);
  }
  // pick the form on the rounded value so "1.0 year" stays singular
  const rounded = Number(count.toFixed(maxDigits));
  const template = forms[rules.select(rounded)] ?? forms.other;
  return fill(template, { count: formatNumber(lang, count, maxDigits, minDigits) });
}

/** A body's name in the language, or the catalogue name when it has none of its own. */
export function bodyName(lang: Lang, body: { id: string; name: string }): string {
  return MESSAGES[lang].bodyNames[body.id] ?? body.name;
}

/** Sets `<html lang>`, the page title and the meta description. */
export function applyDocumentLang(lang: Lang): void {
  const m = MESSAGES[lang];
  document.documentElement.lang = lang;
  document.title = m.page.title;
  document.querySelector('meta[name="description"]')?.setAttribute("content", m.page.description);
}
